<script>
    import { onMount } from 'svelte';
	import { writable } from 'svelte/store';
	import { Circle2 } from 'svelte-loading-spinners';
    import { navigate } from 'svelte-routing';
    import { currentUser } from '../../store/store.js';
	import TopBar from '../TopBar.svelte';
	import { handleGoogleLogin, handleLogin } from '../../utils/login.js';

    onMount(async () => {
        usernameInput.focus(); // Focus the input element
        usernameInput.select();
	});

	// Create the script element
	const googleScript = document.createElement('script');
	googleScript.src = 'https://accounts.google.com/gsi/client';
	googleScript.async = true;
	googleScript.defer = true;

	// Attach the onload event
	googleScript.onload = () => {
		// Initialize Google One Tap
		window.google.accounts.id.initialize({
			client_id: '324523837899-0l8ak628ssr088mej9cv48thvqlcmsi5.apps.googleusercontent.com',
			callback: async (res) => {
				handleGoogleLogin(res);
				navigate('/');
			}
		});
		
		// Render the Google Sign-In button
		window.google.accounts.id.renderButton(
			document.getElementById('google-signin-button'),
			{ theme: 'outline', size: 'large' }
		);
	};

	// Append the script to the document head (or body)
	document.head.appendChild(googleScript);

	let loginLoading = writable(false); 

	let username = '';
	let usernameInput = null;
    let loginPassword = '';
	let errorMessage = '';
	let createAccount = false;
	let email = '';
	let password = '';
	let firstName = '';
	let lastName = '';
	let business = false;
	let passwordError = '';
	let emailError = '';
	let firstNameError = '';
	let lastNameError = '';
	let businessError = '';
	let formError = '';
	let emailInput;
	let passwordInput;
	let firstNameInput;
	let lastNameInput;

    function toggleCreateAccount() {
		createAccount = !createAccount;
	}

    async function handleCreateAccount() {
		// Reset errors
		passwordError = '';
		emailError = '';
		firstNameError = '';
		lastNameError = '';
		businessError = false;
		formError = '';

		// Basic email validation
		if (!validateEmail(email)) {
			emailError = 'Ogiltig e-postadress.';
			return;
		}

		if (password.length < 8) {
			passwordError = 'Lösenordet måste vara minst 8 tecken långt.';
			return;
		}

		if (firstName.length <= 0) {
			firstNameError = 'Var vänlig ange ett förnamn.';
			return;
		}

		if (lastName.length <= 0) {
			lastNameError = 'Var vänlig ange ett efternamn.';
			return;
		}

		if (business !== false && business !== true) {
			businessError = 'Var vänlig att ange om du representerar ett företag eller ej.';
			return;
		}

		// Simulate account creation
		try {
			loginLoading.set(true);

			// The backend expects query parameters, not JSON body
			const url = new URL('https://boanalys-backend-324523837899.europe-north1.run.app/create_account');
			url.searchParams.append('username', email);
			url.searchParams.append('password', password);
			url.searchParams.append('first_name', firstName);
			url.searchParams.append('last_name', lastName);
			url.searchParams.append('business', business.toString());

			const response = await fetch(url.toString(), {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
				}
			});

			if (!response.ok) {
				const errorData = await response.json();
				if (response.status === 400) {
					// Username already exists
					emailError = errorData.detail || 'Användarnamnet finns redan.';
				} else {
					formError = errorData.detail || 'Ett fel uppstod vid skapandet av kontot.';
				}
				return;
			}

			const data = await response.json();
			console.log('Account created with email:', email);
			// Reset form
			email = '';
			password = '';
			firstName = '';
			lastName = '';
			business = false;
			createAccount = false;
			alert('Konto skapat framgångsrikt!');
		} catch (error) {
			formError = 'Ett fel uppstod vid skapandet av kontot.';
			console.error('Error creating account:', error);
		} finally {
			loginLoading.set(false);
		}
	}

	function validateEmail(email) {
		const re = /\S+@\S+\.\S+/;
		return re.test(email);
	}

    async function handleThisLogin() {
		loginLoading.set(true);
		errorMessage = ''; // Reset the error message at the start of the function

		try {
			handleLogin(username, loginPassword);
			navigate('/');
		} catch (error) {
			errorMessage = error.message || 'Ett fel uppstod vid inloggning.'; // Set the error message
		} finally {
			loginLoading.set(false);
		}
	}

    function parseJwt(token) {
		try {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			}).join(''));

			return JSON.parse(jsonPayload);
		} catch (e) {
			return null;
		}
	}

</script>


{#if !$currentUser?.accessToken}
<div class="login-box">
	{#if !createAccount}
	<form on:submit|preventDefault={handleThisLogin} class="login custom-form">
		<h3>Logga in</h3>
		<div id="google-signin-button"></div>
		<h4>Användarnamn</h4>
		<input type="text" class="login-input" bind:value={username} bind:this={usernameInput}>
		<h4>Lösenord</h4>
		<input type="password" class="login-input" bind:value={loginPassword}>
		
		{#if $loginLoading}
		<div class="login-loading">
			<Circle2 size="60" color="#FF3E00" unit="px" duration="1s" />
		</div>
		{:else}
		<p></p>
		<button type="submit" class="login-button" disabled={$loginLoading}>Logga in</button>
		{/if}
	
		{#if errorMessage}
		<p class="error-message">{errorMessage}</p>
		{/if}
	</form>
	{/if}
	{#if !createAccount}
		<span class="create-account-text" on:click={toggleCreateAccount}>
		<h4>Skapa konto</h4>
		</span>
	{:else}
		<form on:submit|preventDefault={handleCreateAccount} class="create-account-form">
		<h3>Skapa konto</h3>
		<h4>E-postadress</h4>
		<input
			type="email"
			bind:value={email}
			bind:this={emailInput}
			class="login-input"
			required
		/>
		{#if emailError}
			<div class="error-message">{emailError}</div>
		{/if}
		<h4>Lösenord</h4>
		<input
			type="password"
			bind:value={password}
			bind:this={passwordInput}
			class="login-input"
			required
		/>
		{#if passwordError}
			<div class="error-message">{passwordError}</div>
		{/if}
		<h4>Förnamn</h4>
		<input
			type="first-name"
			bind:value={firstName}
			bind:this={firstNameInput}
			class="login-input"
			required
		/>
		{#if firstNameError}
			<div class="error-message">{firstNameError}</div>
		{/if}
		<h4>Efternamn</h4>
		<input
			type="last-name"
			bind:value={lastName}
			bind:this={lastNameInput}
			class="login-input"
			required
		/>
		{#if lastNameError}
			<div class="error-message">{lastNameError}</div>
		{/if}
		<div class="checkbox-container">
			<input
			id="businessCheckbox"
			type="checkbox"
			class="login-checkbox"
			bind:checked={business}
			/>
			<h4 style="padding-left: 10px;">Företag</h4>
		</div>
		{#if businessError}
			<div class="error-message">{businessError}</div>
		{/if}
		{#if formError}
			<div class="error-message">{formError}</div>
		{/if}
			{#if $loginLoading}
			<div class="login-loading">
			<Circle2 size="60" color="#FF3E00" unit="px" duration="1s" />
			</div>
			{:else}
			<p></p>
			<button type="submit" class="login-button" disabled={$loginLoading}>
				Skapa konto
			</button>
			<span class="create-account-text" on:click={toggleCreateAccount}>
				<h4>Tillbaka</h4>
			</span>
			{/if}
		</form>
	{/if}
</div>
{/if}

